import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Box,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import surveyService, { SurveyQuestion, SurveySessionResponse } from '../services/survey';
import { 
  QuestionRenderer
} from '../components/QuestionRenderer';
import styles from './Survey.module.css';

// Define Question type locally until package is available  
interface Question {
  id: string;
  type: string;
  question: string;
  required?: boolean;
  options?: string[];
}

const Survey: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<SurveySessionResponse | null>(null);
  const [currentAnswer, setCurrentAnswer] = useState<any>(null);

  useEffect(() => {
    if (surveyId) {
      loadSession();
    }
  }, [surveyId]);

  const loadSession = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await surveyService.getSession(surveyId!);
      
      if (data.isComplete) {
        navigate(`/survey/${surveyId}/completed`);
        return;
      }
      
      setSession(data);
      setCurrentAnswer(data.existingAnswer || null);
    } catch (err: any) {
      console.error('Error loading survey session:', err);
      setError(err.response?.data?.error || 'Failed to load survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!session?.question || currentAnswer === null || currentAnswer === undefined) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await surveyService.submitAnswer(
        surveyId!,
        session.question.id,
        currentAnswer,
        session.currentIndex || 0
      );
      
      if (response.isComplete) {
        navigate(`/survey/${surveyId}/completed`);
      } else {
        setSession(response);
        setCurrentAnswer(response.existingAnswer || null);
      }
    } catch (err: any) {
      console.error('Error submitting answer:', err);
      setError(err.response?.data?.error || 'Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNavigateQuestion = async (index: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await surveyService.getQuestionByIndex(surveyId!, index);
      setSession(response);
      setCurrentAnswer(response.existingAnswer || null);
    } catch (err: any) {
      console.error('Error navigating to question:', err);
      setError(err.response?.data?.error || 'Failed to navigate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question: SurveyQuestion) => {
    // Convert SurveyQuestion to Question type for QuestionRenderer
    const questionForRenderer: Question = {
      id: question.id,
      type: question.type as any, // Type conversion
      question: question.question || '',
      required: question.required,
      // Convert options from Record<string, string> to string[] if needed
      options: question.options ? Object.values(question.options) : undefined,
    };

    return (
      <QuestionRenderer
        question={questionForRenderer}
        value={currentAnswer}
        onChange={setCurrentAnswer}
        readonly={submitting}
        hasNextButton={false} // We handle navigation separately
      />
    );
  };


  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading survey...</Typography>
        </Paper>
      </Container>
    );
  }

  if (!session || !session.question) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="error">
            {error || 'Survey not found or has expired. Please contact support if you need assistance.'}
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {session.surveyTitle || 'Patient Feedback Survey'}
        </Typography>
        {session.surveyDescription && (
          <Typography variant="body1" color="text.secondary">
            {session.surveyDescription}
          </Typography>
        )}
      </Box>

      {/* Progress */}
      {session.progress && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Question {session.progress.current} of {session.progress.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round((session.progress.answered / session.progress.total) * 100)}% Complete
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(session.progress.answered / session.progress.total) * 100}
          />
        </Box>
      )}

      {/* Question Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {session.question.question}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {renderQuestion(session.question)}
      </Paper>

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between">
        <Button
          variant="outlined"
          startIcon={<ChevronLeft />}
          onClick={() => handleNavigateQuestion((session.currentIndex || 0) - 1)}
          disabled={!session.canGoBack || submitting}
        >
          Previous
        </Button>

        <Stack direction="row" spacing={1}>
          {session.hasAnswer && session.canGoForward && (
            <Button
              variant="outlined"
              endIcon={<ChevronRight />}
              onClick={() => handleNavigateQuestion((session.currentIndex || 0) + 1)}
              disabled={submitting}
            >
              Skip
            </Button>
          )}
          
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            onClick={handleSubmitAnswer}
            disabled={
              submitting || 
              (session.question.required && (currentAnswer === null || currentAnswer === undefined || currentAnswer === ''))
            }
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              session.currentIndex === (session.totalQuestions || 0) - 1 ? 'Complete Survey' : 'Next'
            )}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
};

export default Survey;